import React, { useState } from 'react';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { InvoiceData, Reservation } from '../types/hospitable';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FileDown, Edit2 } from 'lucide-react';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flex: 1,
  },
  logo: {
    width: 80,
    height: 80,
  },
  title: {
    fontSize: 24,
    marginBottom: 10,
  },
  companyInfo: {
    marginBottom: 20,
    fontSize: 10,
  },
  invoiceInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingBottom: 5,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
    fontSize: 10,
  },
  label: {
    width: '40%',
  },
  value: {
    width: '60%',
    textAlign: 'right',
  },
  table: {
    marginTop: 20,
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#000',
    paddingBottom: 5,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  tableCol: {
    fontSize: 10,
    flex: 1,
  },
  tableColHeader: {
    fontSize: 10,
    flex: 1,
  },
  totalSection: {
    marginTop: 30,
    paddingTop: 10,
    borderTopWidth: 2,
    borderTopColor: '#000',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 5,
  },
  totalLabel: {
    fontSize: 12,
    marginRight: 20,
  },
  totalValue: {
    fontSize: 12,
    minWidth: 80,
    textAlign: 'right',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 8,
    textAlign: 'center',
    color: '#666',
  },
});

const InvoiceDocument: React.FC<{ invoiceData: InvoiceData }> = ({ invoiceData }) => {
  const { reservation, companyInfo, invoiceNumber, issueDate, dueDate } = invoiceData;
  
  const nights = reservation.nights || Math.ceil(
    (new Date(reservation.check_out).getTime() - new Date(reservation.check_in).getTime()) / (1000 * 60 * 60 * 24)
  );
  
  // Calculer le montant de l'hébergement (total - frais - taxes)
  const totalFees = reservation.fees?.reduce((sum, fee) => sum + fee.amount, 0) || 0;
  const totalTaxes = reservation.taxes?.reduce((sum, tax) => sum + tax.amount, 0) || 0;
  const accommodationAmount = reservation.accommodation_amount || 
    (reservation.total_amount - totalFees - totalTaxes);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.title}>FACTURE</Text>
            <View style={styles.companyInfo}>
              <Text>{companyInfo.name}</Text>
              <Text>{companyInfo.address}</Text>
              <Text>Email: {companyInfo.email}</Text>
              <Text>Tél: {companyInfo.phone}</Text>
              {companyInfo.siret && <Text>SIRET: {companyInfo.siret}</Text>}
            </View>
          </View>
          {companyInfo.logoUrl && (
            <Image style={styles.logo} src={window.location.origin + companyInfo.logoUrl} />
          )}
        </View>

        <View style={styles.invoiceInfo}>
          <View>
            <Text style={{ fontSize: 12, marginBottom: 5 }}>Facture N°: {invoiceNumber}</Text>
            <Text style={{ fontSize: 10 }}>Date: {format(new Date(issueDate), 'dd/MM/yyyy')}</Text>
            <Text style={{ fontSize: 10 }}>Échéance: {format(new Date(dueDate), 'dd/MM/yyyy')}</Text>
          </View>
          
          <View>
            <Text style={{ fontSize: 12, marginBottom: 5 }}>Client:</Text>
            <Text style={{ fontSize: 10 }}>{reservation.guest_name}</Text>
            {reservation.guest_email && <Text style={{ fontSize: 10 }}>{reservation.guest_email}</Text>}
            {reservation.guest_phone && <Text style={{ fontSize: 10 }}>{reservation.guest_phone}</Text>}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Détails de la Réservation</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Propriété:</Text>
            <Text style={styles.value}>{reservation.property_name}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Code de confirmation:</Text>
            <Text style={styles.value}>{reservation.confirmation_code || reservation.id}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Check-in:</Text>
            <Text style={styles.value}>
              {format(new Date(reservation.check_in), 'dd MMMM yyyy', { locale: fr })}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Check-out:</Text>
            <Text style={styles.value}>
              {format(new Date(reservation.check_out), 'dd MMMM yyyy', { locale: fr })}
            </Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableColHeader, { flex: 3 }]}>Description</Text>
            <Text style={styles.tableColHeader}>Quantité</Text>
            <Text style={styles.tableColHeader}>Prix unitaire</Text>
            <Text style={styles.tableColHeader}>Total</Text>
          </View>
          
          <View style={styles.tableRow}>
            <Text style={[styles.tableCol, { flex: 3 }]}>
              Hébergement - {reservation.property_name}
            </Text>
            <Text style={styles.tableCol}>{nights} nuits</Text>
            <Text style={styles.tableCol}>{(accommodationAmount / nights).toFixed(2)} €</Text>
            <Text style={styles.tableCol}>{accommodationAmount.toFixed(2)} €</Text>
          </View>
          
          {reservation.fees && reservation.fees.map((fee, index) => (
            <View key={`fee-${index}`} style={styles.tableRow}>
              <Text style={[styles.tableCol, { flex: 3 }]}>{fee.label}</Text>
              <Text style={styles.tableCol}>1</Text>
              <Text style={styles.tableCol}>{fee.amount.toFixed(2)} €</Text>
              <Text style={styles.tableCol}>{fee.amount.toFixed(2)} €</Text>
            </View>
          ))}
          
          {reservation.taxes && reservation.taxes.map((tax, index) => (
            <View key={`tax-${index}`} style={styles.tableRow}>
              <Text style={[styles.tableCol, { flex: 3 }]}>{tax.label}</Text>
              <Text style={styles.tableCol}>1</Text>
              <Text style={styles.tableCol}>{tax.amount.toFixed(2)} €</Text>
              <Text style={styles.tableCol}>{tax.amount.toFixed(2)} €</Text>
            </View>
          ))}
        </View>

        <View style={styles.totalSection}>
          <View style={styles.totalRow}>
            <Text style={[styles.totalLabel, { fontSize: 14 }]}>Total à payer:</Text>
            <Text style={[styles.totalValue, { fontSize: 14 }]}>{reservation.total_amount.toFixed(2)} €</Text>
          </View>
        </View>

        <Text style={styles.footer}>
          Merci pour votre confiance. Cette facture est payable à réception.
        </Text>
      </Page>
    </Document>
  );
};

interface BulkInvoiceGeneratorProps {
  reservation: Reservation;
  companyInfo: any;
}

const BulkInvoiceGenerator: React.FC<BulkInvoiceGeneratorProps> = ({ reservation, companyInfo }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [clientName, setClientName] = useState(reservation.guest_name || 'Client');
  const [clientEmail, setClientEmail] = useState(reservation.guest_email || '');
  
  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}-${random}`;
  };

  // Créer une copie de la réservation avec le nom client personnalisé
  const customReservation = {
    ...reservation,
    guest_name: clientName,
    guest_email: clientEmail,
  };

  const invoiceData: InvoiceData = {
    reservation: customReservation,
    invoiceNumber: generateInvoiceNumber(),
    issueDate: new Date().toISOString(),
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    companyInfo,
  };

  const fileName = `facture-${reservation.confirmation_code || reservation.id}-${format(new Date(), 'yyyyMMdd')}.pdf`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Section éditable pour le client */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px',
        padding: '8px',
        background: '#f8f9fa',
        borderRadius: '6px',
        fontSize: '14px'
      }}>
        {isEditing ? (
          <>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Nom du client"
              style={{
                flex: 1,
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="Email (optionnel)"
              style={{
                flex: 1,
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
            <button
              onClick={() => setIsEditing(false)}
              style={{
                padding: '4px 8px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              OK
            </button>
          </>
        ) : (
          <>
            <span style={{ flex: 1 }}>
              <strong>Client:</strong> {clientName}
              {clientEmail && <span style={{ color: '#666', marginLeft: '8px' }}>({clientEmail})</span>}
            </span>
            <button
              onClick={() => setIsEditing(true)}
              style={{
                padding: '4px 8px',
                background: 'transparent',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
              }}
            >
              <Edit2 size={14} />
              Modifier
            </button>
          </>
        )}
      </div>
      
      {/* Bouton de génération PDF */}
      <PDFDownloadLink 
        document={<InvoiceDocument invoiceData={invoiceData} />} 
        fileName={fileName}
        className="download-button"
        style={{
          padding: '8px 16px',
          background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '6px',
          fontWeight: '600',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
        }}
      >
        {({ blob, url, loading, error }) => (
          <>
            <FileDown size={16} />
            <span>{loading ? 'Génération...' : 'Générer PDF'}</span>
          </>
        )}
      </PDFDownloadLink>
    </div>
  );
};

export default BulkInvoiceGenerator;